import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { CalendarBlank, Check, Confetti, FloppyDisk, LockKey, LockKeyOpen, PaperPlaneTilt } from "@phosphor-icons/react";
import BranchFilter from "@/components/BranchFilter";

const STATUSES = [
  { v: "present", label: "P", color: "bg-[#00B341] text-white", full: "Present" },
  { v: "half_day", label: "½", color: "bg-amber-500 text-white", full: "Half day" },
  { v: "absent", label: "A", color: "bg-destructive text-white", full: "Absent" },
  { v: "week_off", label: "WO", color: "bg-canvas-panel text-ink", full: "Week off" },
  { v: "holiday", label: "H", color: "bg-sky-600 text-white", full: "Holiday" },
  { v: "leave", label: "L", color: "bg-violet-500 text-white", full: "Leave" },
  { v: "lop", label: "LOP", color: "bg-neutral-800 text-white", full: "Loss of pay" },
];

function StatusPicker({ value, onChange, testId }) {
  return (
    <div className="flex items-center gap-0.5 flex-wrap" data-testid={testId}>
      {STATUSES.map((s) => (
        <button
          key={s.v}
          type="button"
          onClick={() => onChange(s.v)}
          title={s.full}
          data-testid={`${testId}-${s.v}`}
          className={`w-8 h-8 text-[10px] font-mono uppercase font-semibold border border-canvas-line ${
            value === s.v ? s.color : "bg-canvas hover:bg-canvas-panel"
          }`}
        >
          {s.label}
        </button>
      ))}
    </div>
  );
}

export default function HrAttendancePage() {
  const { user } = useAuth();
  const canUnlock = user?.role === "admin" || (user?.permissions || []).includes("hr.attendance_unlock");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  // Admin-only branch narrow. Empty string = all branches (default HR/admin
  // behaviour). Managers see this as a locked badge — backend still enforces.
  const [branchId, setBranchId] = useState("");
  const [employees, setEmployees] = useState([]);
  const [attendance, setAttendance] = useState({});  // employee_id → status
  const [notesById, setNotesById] = useState({});
  const [checkInById, setCheckInById] = useState({});   // employee_id → HH:MM
  const [checkOutById, setCheckOutById] = useState({}); // employee_id → HH:MM
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [showReport, setShowReport] = useState(null);
  // Holidays for the picked year — used to (a) show a "Holiday" banner
  // on the picked date, and (b) compute OT badges (mins worked when
  // Present on a Sunday/holiday).
  const [holidaysByDate, setHolidaysByDate] = useState({});

  const activeEmployees = useMemo(
    () => employees.filter((e) => e.status !== "terminated" && e.status !== "resigned"),
    [employees],
  );

  // Late-minute computation runs client-side for instant UX; the server
  // re-computes on save so the badge can't be spoofed. Grace = 15 min
  // (matches LATE_GRACE_MINUTES on the backend).
  const GRACE = 15;
  const hhmmToMin = (s) => {
    if (!s) return null;
    const [h, m] = String(s).split(":");
    const hi = parseInt(h, 10); const mi = parseInt(m, 10);
    if (Number.isNaN(hi) || Number.isNaN(mi)) return null;
    return hi * 60 + mi;
  };
  const lateMinsFor = (emp) => {
    if (!emp.shift_start) return 0;
    const ci = hhmmToMin(checkInById[emp.id]);
    const ss = hhmmToMin(emp.shift_start);
    if (ci == null || ss == null) return 0;
    return Math.max(0, ci - ss - GRACE);
  };

  // Sunday and holiday helpers — used to (a) skip late flagging (a Sunday
  // arrival isn't "late", shifts don't apply), (b) show an OT badge
  // computed from check_in–check_out for any Present mark on a
  // non-working day.
  const isSundayISO = (iso) => {
    if (!iso) return false;
    const [y, m, d] = iso.split("-").map((x) => parseInt(x, 10));
    if (!y || !m || !d) return false;
    return new Date(Date.UTC(y, m - 1, d)).getUTCDay() === 0;
  };
  const dateIsSunday = useMemo(() => isSundayISO(date), [date]);
  const dateHoliday = holidaysByDate[date] || null;
  const dateIsNonWorking = dateIsSunday || !!dateHoliday;
  const otMinsFor = (emp) => {
    if (!dateIsNonWorking) return 0;
    if (attendance[emp.id] !== "present") return 0;
    const ci = hhmmToMin(checkInById[emp.id]);
    const co = hhmmToMin(checkOutById[emp.id]);
    if (ci == null || co == null || co <= ci) return 0;
    return co - ci;
  };
  const fmtHoursMinutes = (mins) => {
    if (!mins || mins <= 0) return "";
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    if (h === 0) return `${m}m`;
    if (m === 0) return `${h}h`;
    return `${h}h ${m}m`;
  };

  // A submission for THIS user's branch (or the single scoped branch) on
  // this date means the day is closed — locked-icon UI + disabled inputs.
  // For admins viewing multiple branches, we don't lock the whole grid
  // (they can still edit any branch); the "Submit day" button becomes
  // a per-branch dropdown instead.
  const branchIds = useMemo(() => {
    const s = new Set();
    activeEmployees.forEach((e) => { if (e.branch_id) s.add(e.branch_id); });
    return Array.from(s);
  }, [activeEmployees]);

  const mySubmissionsByBranch = useMemo(() => {
    const m = {};
    submissions.filter((s) => s.date === date).forEach((s) => { m[s.branch_id] = s; });
    return m;
  }, [submissions, date]);

  const isLockedForEmployee = (emp) => {
    if (canUnlock) return false;
    const sub = mySubmissionsByBranch[emp.branch_id];
    return !!(sub && sub.locked);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const year = parseInt(date.slice(0, 4), 10) || new Date().getFullYear();
      const empParams = { limit: 500, skip: 0 };
      const attParams = { date_from: date, date_to: date };
      const subParams = { date_from: date, date_to: date };
      if (branchId) {
        empParams.branch_id = branchId;
        attParams.branch_id = branchId;
        subParams.branch_id = branchId;
      }
      const [empRes, attRes, subRes, holRes] = await Promise.all([
        api.get("/hr/employees", { params: empParams }),
        api.get("/hr/attendance", { params: attParams }),
        api.get("/hr/attendance/submissions", { params: subParams }),
        api.get("/hr/holidays", { params: { year } }),
      ]);
      setEmployees(empRes.data);
      const map = {};
      const notes = {};
      const ci = {};
      const co = {};
      (attRes.data || []).forEach((a) => {
        map[a.employee_id] = a.status;
        if (a.notes) notes[a.employee_id] = a.notes;
        if (a.check_in) ci[a.employee_id] = a.check_in;
        if (a.check_out) co[a.employee_id] = a.check_out;
      });
      setAttendance(map);
      setNotesById(notes);
      setCheckInById(ci);
      setCheckOutById(co);
      setSubmissions(subRes.data || []);
      const hMap = {};
      (holRes.data || []).forEach((h) => { hMap[h.date] = h; });
      setHolidaysByDate(hMap);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [date, branchId]);

  useEffect(() => { load(); }, [load]);

  const setStatus = (empId, status) => setAttendance((prev) => ({ ...prev, [empId]: status }));
  const setNote = (empId, note) => setNotesById((prev) => ({ ...prev, [empId]: note }));
  const setCheckIn = (empId, v) => setCheckInById((prev) => ({ ...prev, [empId]: v }));
  const setCheckOut = (empId, v) => setCheckOutById((prev) => ({ ...prev, [empId]: v }));

  const markAllPresent = () => {
    const next = { ...attendance };
    activeEmployees.forEach((e) => { if (!next[e.id]) next[e.id] = "present"; });
    setAttendance(next);
  };

  // On a Sunday/holiday, one-click to mark every unmarked employee as
  // Week Off (default) — the exception path is Present, which then
  // records OT for the check-in/check-out block.
  const markAllWeekOff = () => {
    const next = { ...attendance };
    activeEmployees.forEach((e) => { if (!next[e.id]) next[e.id] = "week_off"; });
    setAttendance(next);
  };

  // Bulk-fill from shift — for every employee marked Present who has a
  // shift assigned, fill check_in / check_out with their shift times.
  // Manager only corrects the outliers (late arrivals, half-days).
  const bulkFillFromShift = () => {
    const nextCi = { ...checkInById };
    const nextCo = { ...checkOutById };
    let filled = 0;
    activeEmployees.forEach((e) => {
      if (!e.shift_start || !e.shift_end) return;
      if (attendance[e.id] !== "present") return;
      if (!nextCi[e.id]) { nextCi[e.id] = e.shift_start; filled += 1; }
      if (!nextCo[e.id]) { nextCo[e.id] = e.shift_end; }
    });
    setCheckInById(nextCi);
    setCheckOutById(nextCo);
    toast.success(`Filled shift times for ${filled} employee${filled === 1 ? "" : "s"}`);
  };

  const save = async () => {
    setSaving(true);
    try {
      const marks = activeEmployees
        .filter((e) => attendance[e.id])
        .map((e) => ({
          employee_id: e.id,
          status: attendance[e.id],
          notes: notesById[e.id] || "",
          check_in: checkInById[e.id] || null,
          check_out: checkOutById[e.id] || null,
        }));
      if (marks.length === 0) {
        toast.error("Nothing to save — mark at least one employee first");
        return;
      }
      const { data } = await api.post("/hr/attendance/bulk", { date, marks });
      toast.success(`Saved attendance for ${data.marked} employee(s) on ${date}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const counts = useMemo(() => {
    const c = {};
    Object.values(attendance).forEach((s) => { c[s] = (c[s] || 0) + 1; });
    // Late = any Present employee whose check_in > shift_start + grace,
    // BUT only on regular working days (Sunday/holiday attendance never
    // counts as "late" — it's OT instead).
    // OT = count of Present marks on a Sunday/holiday. Check-in/out
    // times are ONLY for lateness tracking, never for OT computation.
    let late = 0;
    let otCount = 0;
    activeEmployees.forEach((e) => {
      if (attendance[e.id] !== "present") return;
      if (dateIsNonWorking) {
        otCount += 1;
      } else if (lateMinsFor(e) > 0) {
        late += 1;
      }
    });
    c._late = late;
    c._ot_count = otCount;
    return c;
  }, [attendance, activeEmployees, checkInById, checkOutById, dateIsNonWorking, lateMinsFor]);

  const submitDay = async (branch_id) => {
    if (!window.confirm(`Submit ${date} for close-of-business? Once submitted, only HR/admin can re-open the day for edits.`)) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/hr/attendance/submit-day", { date, branch_id: branch_id || undefined });
      toast.success(`Submitted · ${data.counts?.present || 0} present, ${data.counts?.absent || 0} absent, ${data.counts?.leave || 0} on leave.`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const unlockDay = async (sub) => {
    const reason = window.prompt(`Reason for unlocking ${sub.branch_name} on ${sub.date}? (optional but recommended for audit)`) || "";
    try {
      await api.post("/hr/attendance/unlock-day", {
        branch_id: sub.branch_id, date: sub.date, reason,
      });
      toast.success("Day unlocked — branch manager can now edit again");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // For a branch manager (single-branch scope), pick the branch_id from
  // their employees (they'll all share it). For admins, we'll expose a
  // per-branch button in the submissions strip.
  const managerBranchId = branchIds.length === 1 ? branchIds[0] : null;
  const managerSubmission = managerBranchId ? mySubmissionsByBranch[managerBranchId] : null;
  const dayFullyLocked = !!(managerSubmission && managerSubmission.locked && !canUnlock);

  return (
    <div>
      <PageHeader
        eyebrow="HR / 02"
        title="Attendance"
        subtitle="Daily attendance grid. Pick a date, click each employee's status (P / ½ / A / WO / H / L / LOP), then Save. Idempotent — resaving overwrites yesterday's marks."
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            {dateIsNonWorking ? (
              <button
                onClick={markAllWeekOff}
                disabled={dayFullyLocked}
                data-testid="hr-att-mark-all-wo"
                className="px-3 py-2.5 border border-canvas-line text-sm hover:border-ink flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                title="Default all unmarked employees to Week Off for this holiday/Sunday"
              >
                <Check size={13} weight="bold" />
                Mark blanks Week Off
              </button>
            ) : (
              <button
                onClick={markAllPresent}
                disabled={dayFullyLocked}
                data-testid="hr-att-mark-all-present"
                className="px-3 py-2.5 border border-canvas-line text-sm hover:border-ink flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Check size={13} weight="bold" />
                Mark blanks Present
              </button>
            )}
            <button
              onClick={bulkFillFromShift}
              disabled={dayFullyLocked}
              data-testid="hr-att-bulk-fill-shift"
              className="px-3 py-2.5 border border-canvas-line text-sm hover:border-ink flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
              title="Fill check-in/out with shift times for every Present employee who has a shift"
            >
              <CalendarBlank size={13} weight="bold" />
              Bulk-fill shift times
            </button>
            <button
              onClick={save}
              disabled={saving || dayFullyLocked}
              data-testid="hr-att-save"
              className="px-4 py-2.5 border border-ink text-sm font-semibold hover:bg-canvas-panel disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <FloppyDisk size={14} weight="bold" />
              {saving ? "Saving…" : "Save Day"}
            </button>
            {managerBranchId && (
              <button
                onClick={() => submitDay(managerBranchId)}
                disabled={submitting || dayFullyLocked}
                data-testid="hr-att-submit-day"
                className="bg-ink text-white px-4 py-2.5 text-sm font-semibold hover:bg-brand disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <PaperPlaneTilt size={14} weight="bold" />
                {submitting ? "Submitting…" : dayFullyLocked ? "Submitted ✓" : "Submit Day & Report"}
              </button>
            )}
          </div>
        }
      />

      <div className="px-8 py-6 space-y-4">
        {/* Holiday / Sunday banner — makes it visually obvious the picked
             date is non-working, so managers know to default to WO and
             that any Present mark will be counted as OT. */}
        {dateIsNonWorking && (
          <div className="border-2 border-amber-500 bg-amber-50 px-4 py-3 flex items-center gap-3" data-testid="hr-att-holiday-banner">
            <Confetti size={20} weight="fill" className="text-amber-600" />
            <div className="flex-1">
              <div className="text-sm font-semibold text-amber-900">
                {dateHoliday ? `Holiday: ${dateHoliday.name}` : "Weekly Off (Sunday)"}
              </div>
              <div className="text-xs text-amber-800 font-mono mt-0.5">
                Non-working day. Default status is Week Off. Any employee marked Present will have their check-in–check-out time recorded as OT (overtime).
              </div>
            </div>
          </div>
        )}

        {/* Locked day banner for branch managers */}
        {dayFullyLocked && (
          <div className="border-2 border-amber-500 bg-amber-50 px-4 py-3 flex items-center gap-3" data-testid="hr-att-locked-banner">
            <LockKey size={18} weight="bold" className="text-amber-600" />
            <div className="flex-1">
              <div className="text-sm font-semibold text-amber-900">Day submitted &amp; locked</div>
              <div className="text-xs text-amber-800 font-mono mt-0.5">
                Submitted by {managerSubmission?.submitted_by} at {(managerSubmission?.submitted_at || "").slice(0, 16).replace("T", " ")}. Ask HR/admin to unlock if you need to make changes.
              </div>
            </div>
          </div>
        )}

        {/* "Today's submissions" strip — visible to admin/HR to see which
             branches have closed the day. Also shows an unlock button. */}
        {canUnlock && submissions.length > 0 && (
          <div className="border border-canvas-line p-4 bg-canvas-panel/30" data-testid="hr-att-submissions-strip">
            <div className="text-[10px] uppercase tracking-widest text-ink-muted font-mono mb-2">Branch Submissions · {date}</div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {submissions.map((s) => (
                <div key={s.id} className="border border-canvas-line bg-canvas p-3" data-testid={`hr-att-sub-${s.branch_id}`}>
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-semibold">{s.branch_name}</div>
                    {s.locked ? <LockKey size={12} weight="bold" className="text-[#00B341]" /> : <LockKeyOpen size={12} weight="bold" className="text-amber-500" />}
                  </div>
                  <div className="font-mono text-[10px] text-ink-muted mt-1">
                    P {s.counts?.present || 0} · A {s.counts?.absent || 0} · L {s.counts?.leave || 0} · LOP {s.counts?.lop || 0}
                    {(s.counts?.unmarked || 0) > 0 && <span className="text-destructive"> · {s.counts.unmarked} unmarked</span>}
                  </div>
                  <div className="font-mono text-[9px] text-ink-subtle mt-0.5">by {s.submitted_by}</div>
                  <div className="flex items-center gap-1 mt-2">
                    <button
                      onClick={() => setShowReport(s)}
                      data-testid={`hr-att-view-report-${s.branch_id}`}
                      className="flex-1 px-2 py-1 border border-canvas-line text-[10px] font-mono uppercase tracking-wider hover:border-ink"
                    >
                      View Report
                    </button>
                    {s.locked && (
                      <button
                        onClick={() => unlockDay(s)}
                        data-testid={`hr-att-unlock-${s.branch_id}`}
                        className="px-2 py-1 border border-amber-500 text-amber-600 text-[10px] font-mono uppercase tracking-wider hover:bg-amber-500 hover:text-white"
                      >
                        Unlock
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Date picker + branch filter + counts */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 border border-canvas-line px-3 py-1.5 bg-canvas">
            <CalendarBlank size={13} className="text-ink-muted" />
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              data-testid="hr-att-date"
              className="bg-transparent text-sm focus:outline-none"
            />
          </div>
          <BranchFilter value={branchId} onChange={setBranchId} testId="hr-att-branch" />
          <div className="flex items-center gap-2 flex-wrap ml-auto">
            {STATUSES.map((s) => (
              <div key={s.v} className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider text-ink-muted">
                <span className={`inline-block w-5 h-5 text-center leading-5 text-[9px] font-semibold border border-canvas-line ${s.color}`}>{s.label}</span>
                {s.full} <b className="text-ink">{counts[s.v] || 0}</b>
              </div>
            ))}
            {counts._late > 0 && (
              <div className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider text-destructive" data-testid="hr-att-kpi-late">
                <span className="inline-block w-5 h-5 text-center leading-5 text-[9px] font-semibold border border-destructive bg-destructive text-white">L!</span>
                Late <b>{counts._late}</b>
              </div>
            )}
            {counts._ot_count > 0 && (
              <div className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider text-emerald-700" data-testid="hr-att-kpi-ot">
                <span className="inline-block w-5 h-5 text-center leading-5 text-[9px] font-semibold border border-emerald-700 bg-emerald-700 text-white">OT</span>
                <b>{counts._ot_count}</b>
              </div>
            )}
          </div>
        </div>

        {/* Grid */}
        <div className="bg-canvas-card border border-canvas-line overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="hr-attendance-table">
              <thead>
                <tr className="border-b border-canvas-line bg-canvas-panel">
                  <th className="text-left px-3 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">Code</th>
                  <th className="text-left px-3 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">Name</th>
                  <th className="text-left px-3 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">Work Area</th>
                  <th className="text-left px-3 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">Status</th>
                  <th className="text-left px-3 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">Shift · In · Out</th>
                  <th className="text-left px-3 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">Note</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={6} className="px-4 py-8 text-center font-mono text-xs text-ink-subtle">Loading…</td></tr>
                )}
                {!loading && activeEmployees.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-12 text-center font-mono text-xs text-ink-subtle">No active employees. Go to HR — Employees to add or import.</td></tr>
                )}
                {activeEmployees.map((e) => {
                  const locked = isLockedForEmployee(e);
                  const hasShift = !!(e.shift_start && e.shift_end);
                  // Late only applies on regular working days; OT only on
                  // Sundays/holidays. They're mutually exclusive by design.
                  const late = hasShift && !dateIsNonWorking ? lateMinsFor(e) : 0;
                  // OT badge = employee marked Present on a Sunday/holiday.
                  // Count-based; check-in/out are ONLY for lateness.
                  const isOt = dateIsNonWorking && attendance[e.id] === "present";
                  return (
                    <tr key={e.id} className={`border-b border-canvas-line hover:bg-canvas-panel/30 ${locked ? "opacity-60" : ""}`} data-testid={`hr-att-row-${e.id}`}>
                      <td className="px-3 py-2 font-mono text-[11px]">{e.employee_code}</td>
                      <td className="px-3 py-2">
                        <div className="text-sm font-semibold flex items-center gap-1.5">
                          {e.name}
                          {locked && <LockKey size={11} weight="bold" className="text-amber-600" title="Day locked" />}
                        </div>
                        <div className="font-mono text-[10px] text-ink-muted">{e.designation || "—"}</div>
                      </td>
                      <td className="px-3 py-2 text-sm">{e.work_area || "—"}</td>
                      <td className="px-3 py-2">
                        <fieldset disabled={locked} className={locked ? "cursor-not-allowed" : ""}>
                          <StatusPicker
                            value={attendance[e.id] || ""}
                            onChange={(v) => setStatus(e.id, v)}
                            testId={`hr-att-status-${e.id}`}
                          />
                        </fieldset>
                      </td>
                      <td className="px-3 py-2">
                        {hasShift ? (
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="font-mono text-[10px] text-ink-muted tabular-nums whitespace-nowrap">{e.shift_start}–{e.shift_end}</span>
                            <input
                              type="time"
                              value={checkInById[e.id] || ""}
                              onChange={(ev) => setCheckIn(e.id, ev.target.value)}
                              disabled={locked}
                              data-testid={`hr-att-in-${e.id}`}
                              className="px-1.5 py-0.5 bg-canvas border border-canvas-line text-xs w-[80px] disabled:bg-canvas-panel/60"
                              title="Check-in"
                            />
                            <input
                              type="time"
                              value={checkOutById[e.id] || ""}
                              onChange={(ev) => setCheckOut(e.id, ev.target.value)}
                              disabled={locked}
                              data-testid={`hr-att-out-${e.id}`}
                              className="px-1.5 py-0.5 bg-canvas border border-canvas-line text-xs w-[80px] disabled:bg-canvas-panel/60"
                              title="Check-out"
                            />
                            {late > 0 && attendance[e.id] === "present" && (
                              <span
                                data-testid={`hr-att-late-${e.id}`}
                                className="inline-block px-1.5 py-0.5 bg-destructive text-white text-[10px] font-mono font-semibold rounded-sm"
                                title={`Arrived ${late} min after shift start + 15 min grace`}
                              >
                                LATE {late}m
                              </span>
                            )}
                            {isOt && (
                              <span
                                data-testid={`hr-att-ot-${e.id}`}
                                className="inline-block px-1.5 py-0.5 bg-emerald-700 text-white text-[10px] font-mono font-semibold rounded-sm"
                                title={`Overtime worked on ${dateHoliday ? dateHoliday.name : "Sunday"}`}
                              >
                                OT
                              </span>
                            )}
                          </div>
                        ) : dateIsNonWorking && attendance[e.id] === "present" ? (
                          // No fixed shift, but working on a holiday → still
                          // allow logging in/out for records (not used for OT pay).
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="font-mono text-[10px] text-ink-muted whitespace-nowrap">OT log</span>
                            <input
                              type="time"
                              value={checkInById[e.id] || ""}
                              onChange={(ev) => setCheckIn(e.id, ev.target.value)}
                              disabled={locked}
                              data-testid={`hr-att-in-${e.id}`}
                              className="px-1.5 py-0.5 bg-canvas border border-canvas-line text-xs w-[80px] disabled:bg-canvas-panel/60"
                              title="Check-in"
                            />
                            <input
                              type="time"
                              value={checkOutById[e.id] || ""}
                              onChange={(ev) => setCheckOut(e.id, ev.target.value)}
                              disabled={locked}
                              data-testid={`hr-att-out-${e.id}`}
                              className="px-1.5 py-0.5 bg-canvas border border-canvas-line text-xs w-[80px] disabled:bg-canvas-panel/60"
                              title="Check-out"
                            />
                            {isOt && (
                              <span
                                data-testid={`hr-att-ot-${e.id}`}
                                className="inline-block px-1.5 py-0.5 bg-emerald-700 text-white text-[10px] font-mono font-semibold rounded-sm"
                                title={`Overtime worked on ${dateHoliday ? dateHoliday.name : "Sunday"}`}
                              >
                                OT
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="font-mono text-[10px] text-ink-subtle">no shift</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="text"
                          value={notesById[e.id] || ""}
                          onChange={(ev) => setNote(e.id, ev.target.value)}
                          disabled={locked}
                          data-testid={`hr-att-note-${e.id}`}
                          placeholder="Optional…"
                          className="w-full px-2 py-1 bg-canvas border border-canvas-line text-xs disabled:bg-canvas-panel/60"
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Report modal — visible when admin/HR clicks "View Report" on a
          branch submission tile. Shows the frozen snapshot HR needs to
          audit end-of-day close-out. */}
      {showReport && (
        <div className="fixed inset-0 z-50 bg-ink/40 flex items-center justify-center p-4" data-testid="hr-att-report-modal">
          <div className="bg-canvas border border-canvas-line max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-canvas border-b border-canvas-line px-6 py-4 flex items-center justify-between z-10">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-ink-muted font-mono">Attendance Report</div>
                <div className="text-lg font-semibold">{showReport.branch_name} · {showReport.date}</div>
                <div className="font-mono text-[10px] text-ink-muted mt-0.5">
                  Submitted by {showReport.submitted_by} · {(showReport.submitted_at || "").slice(0, 19).replace("T", " ")}
                </div>
              </div>
              <button onClick={() => setShowReport(null)} className="px-4 py-2 border border-canvas-line text-sm">Close</button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-4 gap-2">
                {["present", "absent", "half_day", "leave", "lop", "week_off", "holiday", "unmarked"].map((k) => (
                  <div key={k} className="border border-canvas-line p-3 bg-canvas-panel/30">
                    <div className="font-mono text-[9px] uppercase tracking-widest text-ink-muted">{k.replace("_", " ")}</div>
                    <div className="font-display text-xl font-bold tabular-nums mt-1">{showReport.counts?.[k] || 0}</div>
                  </div>
                ))}
              </div>
              {[
                { key: "late_arrivals", label: "Late Arrivals", color: "text-destructive" },
                { key: "absentees", label: "Absent", color: "text-destructive" },
                { key: "leave_takers", label: "On Leave", color: "text-violet-600" },
                { key: "lop_names", label: "LOP", color: "text-neutral-800" },
                { key: "unmarked", label: "Unmarked", color: "text-amber-600" },
              ].map((sec) => (
                (showReport[sec.key] || []).length > 0 && (
                  <div key={sec.key} className="border border-canvas-line" data-testid={`hr-att-report-${sec.key}`}>
                    <div className={`px-3 py-2 bg-canvas-panel/30 font-mono text-[10px] uppercase tracking-widest ${sec.color}`}>
                      {sec.label} · {showReport[sec.key].length}
                    </div>
                    <ul className="p-3 text-sm space-y-1">
                      {showReport[sec.key].map((r, i) => (
                        <li key={i} className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono text-[10px] text-ink-muted min-w-[50px]">{r.employee_code}</span>
                          <span>{r.name}</span>
                          {r.designation && <span className="text-ink-muted text-[11px]">— {r.designation}</span>}
                          {sec.key === "late_arrivals" && (
                            <span className="ml-auto font-mono text-[11px] text-destructive font-semibold">
                              LATE {r.late_minutes}m · in {r.check_in} · shift {r.shift_start}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )
              ))}
              {showReport.note && (
                <div className="border border-canvas-line p-3 bg-canvas-panel/20">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-ink-muted mb-1">Manager's note</div>
                  <div className="text-sm">{showReport.note}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
